<script setup>

import {computed, onMounted, ref, watch} from "vue";

const emit = defineEmits(['update:content'])

const props = defineProps({
    content: {
        type: Array,
        required: false,
        default: () => {
            return {
                top: 'none',
                right: 'none',
                bottom: 'none',
                left: 'none'
            }
        }
    },
    label: {
        type: String,
        required: false
    },
    presets: {
        type: Array,
        required: false,
        default: () => []
    }
})

const editingDimension = ref(null)
const _values = ref(props.content)

const setValue = (dimension, value) => {
    _values.value[dimension] = value
    emit('update:content', _values.value)
    editingDimension.value = null
}

const currentTopLabel = computed(() => {
    return props.presets.find(p => p.value === _values.value.top).label
})

const currentRightLabel = computed(() => {
    return props.presets.find(p => p.value === _values.value.right).label
})

const currentLeftLabel = computed(() => {
    return props.presets.find(p => p.value === _values.value.left).label
})

const currentBottomLabel = computed(() => {
    return props.presets.find(p => p.value === _values.value.bottom).label
})

watch(() => props.content, (newValue) => {
    _values.value = newValue
})

onMounted(() => {

})

</script>
<template>
    <div>
        <div class="grid grid-cols-3 items-center gap-2">
            <div class="bg-gray-100"></div>
            <div class="h-12 p-2">
                <div class="relative">
                    <ul
                        class="flex flex-col items-stretch absolute inset-0"
                        :class="editingDimension === 'top' ? 'z-10' : ''"
                    >
                        <li
                            v-for="(preset, i) in props.presets"
                            :key="i"
                        >
                            <div v-if="editingDimension === 'top'">
                                <button-base
                                    size="sm"
                                    :style-type="_values.top === preset.value ? 'primary' : 'secondary'"
                                    @click="setValue('top', preset.value)"
                                    class="w-full justify-center mb-1"
                                >{{ preset.label }}</button-base>
                            </div>
                        </li>
                        <li>
                            <div
                                v-if="editingDimension !== 'top'"
                                :class="editingDimension !== null ? 'opacity-50' : ''"
                            >
                                <button-base
                                    size="sm"
                                    style-type="secondary"
                                    @click="editingDimension = 'top'"
                                    class="w-full justify-center mb-1"
                                >{{ currentTopLabel }}</button-base>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
            <div class="bg-gray-100"></div>
            <div class="h-12 p-2">
                <div class="relative">
                    <ul
                        class="flex flex-col items-stretch absolute inset-0"
                        :class="editingDimension === 'left' ? 'z-10' : ''"
                    >
                        <li
                            v-for="(preset, i) in props.presets"
                            :key="i"
                        >
                            <div v-if="editingDimension === 'left'">
                                <button-base
                                    size="sm"
                                    :style-type="_values.left === preset.value ? 'primary' : 'secondary'"
                                    @click="setValue('left', preset.value)"
                                    class="w-full justify-center mb-1"
                                >{{ preset.label }}</button-base>
                            </div>
                        </li>
                        <li>
                            <div
                                v-if="editingDimension !== 'left'"
                                :class="editingDimension !== null ? 'opacity-50' : ''"
                            >
                                <button-base
                                    size="sm"
                                    style-type="secondary"
                                    @click="editingDimension = 'left'"
                                    class="w-full justify-center mb-1"
                                >{{ currentLeftLabel }}</button-base>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
            <div class="">
                <div class="border bg-gradient-to-b from-gray-100 to-gray-200 w-full aspect-video"></div>
            </div>
            <div class="h-12 p-2">
                <div class="relative">
                    <ul
                        class="flex flex-col items-stretch absolute inset-0"
                        :class="editingDimension === 'right' ? 'z-10' : ''"
                    >
                        <li
                            v-for="(preset, i) in props.presets"
                            :key="i"
                        >
                            <div v-if="editingDimension === 'right'">
                                <button-base
                                    size="sm"
                                    :style-type="_values.right === preset.value ? 'primary' : 'secondary'"
                                    @click="setValue('right', preset.value)"
                                    class="w-full justify-center mb-1"
                                >{{ preset.label }}</button-base>
                            </div>
                        </li>
                        <li>
                            <div
                                v-if="editingDimension !== 'right'"
                                :class="editingDimension !== null ? 'opacity-50' : ''"
                            >
                                <button-base
                                    size="sm"
                                    style-type="secondary"
                                    @click="editingDimension = 'right'"
                                    class="w-full justify-center mb-1"
                                >{{ currentRightLabel }}</button-base>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
            <div class="bg-gray-100"></div>
            <div class="h-12 p-2">
                <div class="relative">
                    <ul
                        class="flex flex-col items-stretch absolute inset-0"
                        :class="editingDimension === 'bottom' ? 'z-10' : ''"
                    >
                        <li
                            v-for="(preset, i) in props.presets"
                            :key="i"
                        >
                            <div v-if="editingDimension === 'bottom'">
                                <button-base
                                    size="sm"
                                    :style-type="_values.bottom === preset.value ? 'primary' : 'secondary'"
                                    @click="setValue('bottom', preset.value)"
                                    class="w-full justify-center mb-1"
                                >{{ preset.label }}</button-base>
                            </div>
                        </li>
                        <li>
                            <div
                                v-if="editingDimension !== 'bottom'"
                                :class="editingDimension !== null ? 'opacity-50' : ''"
                            >
                                <button-base
                                    size="sm"
                                    style-type="secondary"
                                    @click="editingDimension = 'bottom'"
                                    class="w-full justify-center mb-1"
                                >{{ currentBottomLabel }}</button-base>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>
